import * as ko from 'knockout';
import {IKnockoutComponent, IMessageData, IObjectData, IRuleset, ISystem, ISystemRole} from '../../../../index';
import {global, lm} from '../../../../appContext';
import {KOSubscriptionTracker} from '../../../../util/KOSubscriptionTracker';
import {MAX_RESULTS_PER_PAGE, ObjectTypeId, SYSTEM_MEGA_EADM} from '../../../../constants';
import PromiseRequest from '../../../../req/PromiseRequest';
import {
    createErrorMessage,
    getObjectType,
    getPermissionDisplayName,
    getRawSyncFieldValue,
    isSupported
} from '../../../../util/misc';
import {getSearchObjectParams, getSearchSystemParams} from '../../../../util/request';
import {nsFa} from '../../../fa/fn';
import {getHash, isRoutePermissionOkay} from '../../../../constants/RouteDefinitions';
import {REQUEST_MESSAGE} from '../../../../constants/Language';
import {nsRuleset} from '../../../ruleset/nsRuleset';
import {nsSyncFields} from '../../../../req/nsSyncFields';
import {MultiComponent} from '../../../meta/multi';
import {nsSearchToggle} from '../../../search/nsSearchToggle';
import {ITableDef} from "../../../table/index";
import {TableBasic} from "../../../table/basic";
import {Selected} from "../../../table/selected";
import {nsSystem} from "../../fn";
import {IParams as IEllipsisButtonParams} from "../../../button/ellipsis-dropdown"
import {ISearchObjectRouteOptions} from "@component/object";
import {withHashQuery} from "../../../../util/hashQuery";
import pubsub from "../../../../pubsub";
import i = nsFa.i;
import createRulesetWithRuleForObjectPermissionSystemRole = nsSystem.createRulesetWithRuleForObjectPermissionSystemRole;
import detailsHtml from './details.html?raw';

export let template = detailsHtml;

export class viewModel<T extends ISystem> {
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly req_searchsystem: PromiseRequest = global.getPromiseRequest('searchsystem');
    private readonly req_searchobject__groups: PromiseRequest = global.getPromiseRequest('searchobject');
    private roles: ISystemRole[] = [];
    private system!: T;
    private selectedObjects: KnockoutObservableArray<ISystemRole> = ko.observableArray<ISystemRole>([])

    private groups!: IObjectData[];
    private rulesets!: IRuleset[];
    private tableDef!: TableBasic<ISystemRole>;
    private selectedDef!: Selected<ISystemRole>;

    constructor() {
        pubsub.on(this, 'object.needs.refresh', () => this.init())
        this.init();
    }

    private init() {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});

        this.roles = [];

        Promise.resolve()
            .then(() => {
                return this.req_searchsystem
                    .request(getSearchSystemParams({
                        resultsprpage: MAX_RESULTS_PER_PAGE,
                    }))
                    .then((data: T[]) => {
                        const system = data.find(o => o.id == global.getRouteId());
                        if (!system) {
                            return;
                        }
                        this.system = system;
                        this.roles = this.system.roles;

                        const def: ITableDef<ISystemRole> = {
                            checkbox: {
                                validate: () => this.system.name != SYSTEM_MEGA_EADM && !this.system.definitionreadonly,
                            },
                            columns: [
                                {
                                    key: 'id',
                                    sortable: true
                                },
                                {
                                    key: 'name',
                                    sortable: true,
                                },
                                {
                                    key: 'roleenum',
                                    label: 'external id',
                                    sortable: true,
                                },
                                {
                                    key: 'priority',
                                    sortable: true,
                                    validate: () => !!this.system.prioritybasedroles,
                                },
                                {
                                    contentType: 'component',
                                    getComponent: obj => ({
                                        name: 'component/meta/multi',
                                        params: () => {
                                            const ret = new MultiComponent()
                                            ret.cssClass = 'read-list-memberofgroups'
                                            ret.data = (obj.memberofgroups ?? '')
                                                .split(',')
                                                .filter(Boolean)
                                                .map((id: string) => this.groups.find(o => o.id.toString() == id))
                                                .sort((a, b) => {
                                                    const nameA = a ? getRawSyncFieldValue('Name', a.syncfields) ?? '' : '';
                                                    const nameB = b ? getRawSyncFieldValue('Name', b.syncfields) ?? '' : '';
                                                    return nameA.localeCompare(nameB);
                                                })
                                                .map(o => ({
                                                    name: 'component/variable/object-by-any',
                                                    params: {
                                                        data: o,
                                                        linkable: !this.selectedObjects().length,
                                                        noClickBubble: true,
                                                    },
                                                }))
                                            ret.enableCollapse()
                                            return ret
                                        }
                                    }),
                                    getTitle: () => '',
                                    key: 'memberofgroups',
                                    label: 'join group|p',
                                    show: obj => !!obj.memberofgroups,
                                    sortable: true,
                                },
                                {
                                    contentType: 'component',
                                    getComponent: obj => this.getRulesetListComponent(obj.rolememberrulesets),
                                    key: 'rolememberrulesets',
                                    label: 'receives automatic role',
                                    show: obj => !!obj.rolememberrulesets?.split(',').length,
                                    sortable: true,
                                },
                                {
                                    contentType: 'component',
                                    getComponent: obj => this.getRulesetListComponent(obj.availabletorulesets),
                                    getTitle: () => '',
                                    key: 'availabletorulesets',
                                    label: 'can be given permission',
                                    show: obj => !!obj.availabletorulesets,
                                    sortable: true,
                                },
                                {
                                    getContent: (o, c, v) => isSupported(v as boolean),
                                    key: 'recursive',
                                    label: 'department specific permission',
                                    sortable: true,
                                },
                                {
                                    getContent: (o, c, v) => isSupported(v as boolean),
                                    key: 'purgemanualdepartmentpermissions',
                                    label: 'remove manually assigned department permissions',
                                    sortable: true,
                                    validate: () => this.system.purgemanualdepartmentpermissions,
                                }, {
                                    key: 'systemowner',
                                    label: 'system owner',
                                    contentType: 'html',
                                    getContent: o => o.systemowner
                                        ? o.systemowner
                                            .split(',')
                                            .join('<br>')
                                        : '—',
                                    getTitle: o => o.systemowner,
                                    sortable: true,
                                }, {
                                    key: 'description',
                                    getContent: o => o.description || '—',
                                    getTitle: o => o.description,
                                    sortable: true,
                                }, {
                                    key: 'metadata',
                                    getContent: o => o.metadata || '—',
                                    getTitle: o => o.metadata,
                                    sortable: true,
                                }, {
                                    contentType: 'component',
                                    key: 'func' as keyof ISystemRole,
                                    label: '',
                                    sortable: false,
                                    validate: () => {
                                        return isRoutePermissionOkay('createruleset') && isRoutePermissionOkay('createmessageflow') && !this.selectedObjects().length
                                    },
                                    getComponent: (systemRole, col, val) => {
                                        const params: IEllipsisButtonParams = {
                                            buttons: [
                                                {
                                                    label: lm.t('search for users with this permission'),
                                                    href: () => {
                                                        const options: ISearchObjectRouteOptions = {
                                                            $resetFilters: true,
                                                            roleid: systemRole.id,
                                                            showAll: true,
                                                            systemid: systemRole.systemid,
                                                        };
                                                        return withHashQuery(getHash('usersearch'), {
                                                            resetFilters: options.$resetFilters,
                                                            roleid: options.roleid,
                                                            showAll: options.showAll,
                                                            systemid: options.systemid,
                                                        }) ?? ''
                                                    },
                                                },
                                                {
                                                    label: () => lm.t('Generate ruleset and message flow'),
                                                    href: () => getHash('systemaccesswizardgenerate-rs-and-mf', {
                                                        systemid: systemRole.systemid,
                                                        systemroleid: systemRole.id,
                                                    }) ?? '',
                                                    validator: () => !this.system.definitionreadonly,
                                                },
                                                {
                                                    label: () => lm.t('wizard for assigning permissions'),
                                                    href: () => getHash('wizardpermissions', {
                                                        orgnr: global.login.selectedOrganizationId() ?? '',
                                                        systemid: systemRole.systemid,
                                                        roleid: systemRole.id,
                                                    }) ?? '',
                                                    validator: () => !this.system.definitionreadonly,
                                                },
                                                {
                                                    label: () => lm.t(`create ruleset for users with this permission`),
                                                    onClick: () => createRulesetWithRuleForObjectPermissionSystemRole(systemRole),
                                                },
                                                {
                                                    cls: 'btn-danger btn-ready-calm',
                                                    label: () => lm.t('delete'),
                                                    onClick: () => {
                                                        global.dialogManager.addComponent({
                                                            content: {
                                                                name: 'component/systemaccess/dialog/purge-permission',
                                                                params: {
                                                                    permission: systemRole,
                                                                    system: this.system,
                                                                }
                                                            },
                                                            title: `${i('fa-trash-alt')} ((delete subject|s:((permission|ac)))) <span class="name">[${this.system.name} ▷ ${systemRole.name}]</span>`,
                                                            onSuccess: () => this.init(),
                                                            onError: () => this.init(),
                                                        });
                                                    },
                                                    validator: () => this.system.name != SYSTEM_MEGA_EADM && !this.system.definitionreadonly,
                                                }
                                            ]
                                        };

                                        return {
                                            name: 'component/button/ellipsis-dropdown',
                                            params: params
                                        };
                                    },
                                }
                            ],
                            data: this.roles,
                            header: {
                                getContent: c => lm.t(c.label ?? c.key) ?? '',
                                getTitle: c => lm.t(c.label ?? c.key) ?? '',
                            },
                            navigation: true,
                            rows: {
                                onClick: o => {
                                    new nsSearchToggle.ItemCheckbox<ISystemRole>({
                                        item: o,
                                        key: 'id',
                                        selected: this.selectedObjects,
                                    }).toggle()
                                    return false
                                },
                                checkbox: {
                                    isChecked: o => !!this.selectedObjects().find(_o => o.id == _o.id),
                                    onToggle: o => {
                                        this.selectedObjects().includes(o)
                                            ? this.selectedObjects.remove(o)
                                            : this.selectedObjects.push(o)
                                    },
                                    validate: () => this.system.name != SYSTEM_MEGA_EADM && !this.system.definitionreadonly,
                                },
                            },
                            selected: this.selectedObjects,
                            sort: {
                                ascending: ko.observable(true),
                                sortColumn: ko.observable<keyof ISystemRole>('roleenum'),
                            },
                            translationKey: 'permission',
                        }

                        this.tableDef = new TableBasic(def)
                        def.operationsButton = {
                            mainButton: {
                                label: () => `move s|s:((permission|ac|n:${this.selectedObjects().length}))`,
                                onClick: () => this.onMoveClick(),
                                validator: () => !this.system.definitionreadonly,
                            },
                            buttons: [{
                                cls: 'btn-danger btn-ready-calm',
                                label: `delete subject|s:((permission|ac|n:${this.selectedObjects().length}))`,
                                onClick: () => this.onDeleteClick(),
                                validator: () => !this.system.definitionreadonly,
                            }],
                        }
                        this.selectedDef = new Selected(def)
                    })
            })
            .then(() => {
                return nsSyncFields
                    .getSyncfieldIdsForObjectTypeId(ObjectTypeId.Group)
                    .then((syncfieldids: string) => {
                        const groupIds = this.system.roles.reduce((all: any[], r: ISystemRole) => {
                            const groupIds: string[] = r.memberofgroups.split(',').filter(Boolean);
                            return all.concat(groupIds)
                        }, [])
                        return this.req_searchobject__groups
                            .request(getSearchObjectParams({
                                objecttypeid: ObjectTypeId.Group,
                                resultsprpage: MAX_RESULTS_PER_PAGE,
                                jruleset: JSON.stringify(nsRuleset.generateRuleset({
                                    objecttype: getObjectType(ObjectTypeId.Group),
                                    rules: [nsRuleset.generateRule({
                                        argument: groupIds.join(','),
                                        argumentoperator: 'Er lik en av',
                                        attribute: 'Id',
                                    })],
                                })),
                                syncfieldids: syncfieldids,
                            }))
                            .then((data: IObjectData[]) => {
                                this.groups = data
                            })
                    })
            })
            .then(() => nsRuleset.getAll().then(data => this.rulesets = data))
            .then(() => {
                if (!this.roles.length) {
                    this.messageData({message: 'no permissions'})
                } else {
                    this.messageData(null);
                }
                this.isReady(true);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            })
        ;
    }

    private getRulesetListComponent(ids: string | undefined): IKnockoutComponent {
        return {
            name: 'component/meta/multi',
            params: () => {
                const ret = new MultiComponent()
                ret.cssClass = 'read-list-memberofgroups'
                ret.data = (ids ?? '')
                    .split(',')
                    .filter(Boolean)
                    .map((id: string) => this.rulesets.find(o => o.id.toString() == id))
                    .sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))
                    .map(o => ({
                        name: 'component/variable/ruleset',
                        params: {
                            item: o,
                            linkable: !this.selectedObjects().length,
                            noClickBubble: true,
                        },
                    }))
                return ret
            }
        }
    }

    private getCellSortValue(key: keyof T, val: T[keyof T], item: T) {
        if (key == 'roleenum') {
            return Number(val);
        }
        return val;
    }

    private getCellText(key: keyof ISystemRole, val: ISystemRole[keyof ISystemRole], item: ISystemRole) {
        if (key == 'name') {
            return getPermissionDisplayName(item, this.system)
        }
        return val
    }

    private onMoveClick() {
        const roles = this.selectedObjects();
        global.dialogManager.addComponent({
            content: {
                name: 'component/systemaccess/dialog/move-roles',
                params: {
                    roles: roles,
                    system: this.system,
                }
            },
            title: `${i('fa-server-move')} ((move s|s:((permission|ac|n:${roles.length}))))`,
            onSuccess: () => {
                this.selectedObjects([])
            },
            onError: () => {
                this.selectedObjects([])
            },
        })
    }

    private onDeleteClick() {
        const roles = this.selectedObjects();
        global.dialogManager.addComponent({
            content: {
                name: 'component/systemaccess/dialog/purge-permissions',
                params: {
                    roles: roles,
                }
            },
            title: `${i('fa-trash-alt')} ((delete subject|s:((permission|ac|n:${roles.length}))))`,
            onSuccess: () => {
                this.selectedObjects([])
            },
            onError: () => {
                this.selectedObjects([])
            },
        })
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.req_searchsystem.abort();
        this.req_searchobject__groups.abort();
    }
}
